import axios from 'axios';

const baseUrl = `${process.env.VUE_APP_ADMIN_URL}/client-org-charts`;

const DEFAULT_ERROR_MESSAGE = 'Algo falló. Intenta de nuevo.';

function headers() {
  const accessToken = JSON.parse(localStorage.getItem('accessToken'));
  return { Authorization: `Bearer ${accessToken}` };
}

// Quita los filtros vacíos para no mandar `?search=` ni `?country=null`.
function cleanParams(params = {}) {
  return Object.keys(params).reduce((acc, key) => {
    const value = params[key];
    if (value !== null && value !== undefined && value !== '') acc[key] = value;
    return acc;
  }, {});
}

function ok(response) {
  if (!response || response.status === 204 || !response.data) return { status: 'ok' };
  return { status: 'ok', data: response.data.data, meta: response.data.meta };
}

// El backend responde { error: { code, message, details } }. Se conserva el `code`
// para que las vistas reaccionen a ORG_CHART_CYCLE, POSSIBLE_DUPLICATE_*, etc.
function fail(err) {
  const error = (err && err.response && err.response.data && err.response.data.error) || {};
  return {
    status: 'error',
    code: error.code || null,
    message: error.message || DEFAULT_ERROR_MESSAGE,
    details: error.details || null
  };
}

async function request(method, path, { params, body } = {}) {
  try {
    const config = { headers: headers() };
    if (params) config.params = cleanParams(params);
    const url = `${baseUrl}${path}`;
    let response;
    if (method === 'get' || method === 'delete') {
      response = await axios[method](url, config);
    } else {
      response = await axios[method](url, body || {}, config);
    }
    return ok(response);
  } catch (err) {
    return fail(err);
  }
}

export default class ClientOrgChartsService {
  static getOrganizations({ countryOptionId, typeOptionId, search, page, pageSize } = {}) {
    return request('get', '/organizations', {
      params: { countryOptionId, typeOptionId, search, page, pageSize }
    });
  }

  static getOrganization(pipedriveOrgId) {
    return request('get', `/organizations/${pipedriveOrgId}`);
  }

  static savePosition(pipedriveOrgId, personId, { managerPersonId = null, area = null } = {}) {
    return request('put', `/organizations/${pipedriveOrgId}/positions/${personId}`, {
      body: { managerPersonId, area }
    });
  }

  static removePosition(pipedriveOrgId, personId) {
    return request('delete', `/organizations/${pipedriveOrgId}/positions/${personId}`);
  }

  static getOrganizationNotes(pipedriveOrgId) {
    return request('get', `/organizations/${pipedriveOrgId}/notes`);
  }

  static addOrganizationNote(pipedriveOrgId, text) {
    return request('post', `/organizations/${pipedriveOrgId}/notes`, { body: { text } });
  }

  static editOrganizationNote(pipedriveOrgId, noteId, text) {
    return request('put', `/organizations/${pipedriveOrgId}/notes/${noteId}`, { body: { text } });
  }

  static createPerson(pipedriveOrgId, payload) {
    return request('post', `/organizations/${pipedriveOrgId}/persons`, { body: payload });
  }

  static updatePerson(personId, payload) {
    return request('patch', `/persons/${personId}`, { body: payload });
  }

  static getOrganizationCandidates(pipedriveOrgId) {
    return request('get', `/organizations/${pipedriveOrgId}/candidates`);
  }

  static getArtifactCompanies({ country, search, page, pageSize } = {}) {
    return request('get', '/artifact-companies', { params: { country, search, page, pageSize } });
  }

  static getArtifactCompanyCandidates(companyId) {
    return request('get', `/artifact-companies/${companyId}/candidates`);
  }

  // payload: { pipedriveOrgId } o { create: true, name, skipDuplicateCheck? }
  static linkArtifactCompany(companyId, payload) {
    return request('post', `/artifact-companies/${companyId}/link`, { body: payload });
  }

  static getUnassignedCandidates({ search, page, pageSize } = {}) {
    return request('get', '/candidates/unassigned', { params: { search, page, pageSize } });
  }

  static confirmCandidate(candidateId, payload) {
    return request('post', `/candidates/${candidateId}/confirm`, { body: payload });
  }

  static discardCandidate(candidateId) {
    return request('post', `/candidates/${candidateId}/discard`);
  }

  static searchOrganizations(term) {
    return request('post', '/organizations/search', { body: { term } });
  }

  static dismissDuplicate(pipedriveOrgIdA, pipedriveOrgIdB) {
    return request('post', '/duplicates/dismiss', { body: { pipedriveOrgIdA, pipedriveOrgIdB } });
  }
}
